import { useEffect, useState } from 'react';

// material-ui
import { Grid, Button } from '@mui/material';

import useAuth from 'hooks/useAuth';

// project imports
import { gridSpacing } from 'store/constant';
import { ReactSpreadsheetImport } from 'react-spreadsheet-import';
// ==============================|| DEFAULT DASHBOARD ||============================== //

import PropTypes from 'prop-types';
import React from 'react';

// material-ui
import {
    Box,
    IconButton,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    TablePagination,
    Toolbar,
    Typography,
    Checkbox,
    TableSortLabel,
    Tooltip,
    FormControl,
    InputLabel,
    Select,
    MenuItem
} from '@mui/material';

import { visuallyHidden } from '@mui/utils';

// project imports
import MainCard from 'ui-component/cards/MainCard';
import SecondaryAction from 'ui-component/cards/CardSecondaryAction';
import { CSVExport } from './TableExports';
import { header } from './TableBasic';

// assets
import DeleteIcon from '@mui/icons-material/Delete';

// table data
function createData(main_category, middle_category, subcategory, Name, Price, Source, Url, Info, DataInfo) {
    return {
        main_category,
        middle_category,
        subcategory,
        Name,
        Price,
        Source,
        Url,
        Info,
        DataInfo
    };
}

// table filter
function descendingComparator(a, b, orderBy) {
    if (b[orderBy] < a[orderBy]) {
        return -1;
    }
    if (b[orderBy] > a[orderBy]) {
        return 1;
    }
    return 0;
}

const getComparator = (order, orderBy) =>
    order === 'desc' ? (a, b) => descendingComparator(a, b, orderBy) : (a, b) => -descendingComparator(a, b, orderBy);

function stableSort(array, comparator) {
    const stabilizedThis = array.map((el, index) => [el, index]);
    stabilizedThis.sort((a, b) => {
        const order = comparator(a[0], b[0]);
        if (order !== 0) return order;
        return a[1] - b[1];
    });
    return stabilizedThis.map((el) => el[0]);
}

// table header
const headCells = [
    {
        id: 'main_category',
        numeric: false,
        disablePadding: false,
        label: 'Main Category'
    },
    {
        id: 'middle_category',
        numeric: false,
        disablePadding: false,
        label: 'Middle Category'
    },
    {
        id: 'subcategory',
        numeric: false,
        disablePadding: false,
        label: 'Subcategory'
    },
    {
        id: 'Name',
        numeric: false,
        disablePadding: false,
        label: 'Name'
    },
    {
        id: 'Price',
        numeric: false,
        disablePadding: false,
        label: 'Price'
    },
    {
        id: 'Url',
        numeric: false,
        disablePadding: false,
        label: 'Url'
    },
    {
        id: 'Source',
        numeric: false,
        disablePadding: false,
        label: 'Source'
    },
    {
        id: 'DataInfo',
        numeric: false,
        disablePadding: false,
        label: 'Date'
    },
    {
        id: 'Info',
        numeric: false,
        disablePadding: false,
        label: 'Info'
    }
];

// ==============================|| TABLE - HEADER ||============================== //

function EnhancedTableHead({ onSelectAllClick, order, orderBy, numSelected, rowCount, onRequestSort }) {
    const createSortHandler = (property) => (event) => {
        onRequestSort(event, property);
    };

    return (
        <TableHead>
            <TableRow>
                <TableCell padding="checkbox" sx={{ pl: 3 }}>
                    <Checkbox
                        color="primary"
                        indeterminate={numSelected > 0 && numSelected < rowCount}
                        checked={rowCount > 0 && numSelected === rowCount}
                        onChange={onSelectAllClick}
                        inputProps={{
                            'aria-label': 'select all desserts'
                        }}
                    />
                </TableCell>
                {headCells.map((headCell) => (
                    <TableCell
                        key={headCell.id}
                        align={headCell.numeric ? 'right' : 'left'}
                        padding={headCell.disablePadding ? 'none' : 'normal'}
                        sortDirection={orderBy === headCell.id ? order : false}
                    >
                        <TableSortLabel
                            active={orderBy === headCell.id}
                            direction={orderBy === headCell.id ? order : 'asc'}
                            onClick={createSortHandler(headCell.id)}
                        >
                            {headCell.label}
                            {orderBy === headCell.id ? (
                                <Box component="span" sx={visuallyHidden}>
                                    {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                                </Box>
                            ) : null}
                        </TableSortLabel>
                    </TableCell>
                ))}
            </TableRow>
        </TableHead>
    );
}

EnhancedTableHead.propTypes = {
    numSelected: PropTypes.number.isRequired,
    onRequestSort: PropTypes.func.isRequired,
    onSelectAllClick: PropTypes.func.isRequired,
    order: PropTypes.oneOf(['asc', 'desc']).isRequired,
    orderBy: PropTypes.string.isRequired,
    rowCount: PropTypes.number.isRequired
};

// ==============================|| TABLE - HEADER TOOLBAR ||============================== //

const EnhancedTableToolbar = ({ numSelected }) => (
    <Toolbar
        sx={{
            p: 0,
            pl: 1,
            pr: 1,
            ...(numSelected > 0 && {
                color: (theme) => theme.palette.secondary.main
            })
        }}
    >
        {numSelected > 0 ? (
            <Typography color="inherit" variant="subtitle1">
                {numSelected} selected
            </Typography>
        ) : (
            <Typography variant="h6" id="tableTitle">
                Product Details
            </Typography>
        )}
        <Box sx={{ flexGrow: 1 }} />
        {numSelected > 0 && (
            <Tooltip title="Delete">
                <IconButton size="large">
                    <DeleteIcon />
                </IconButton>
            </Tooltip>
        )}
    </Toolbar>
);

EnhancedTableToolbar.propTypes = {
    numSelected: PropTypes.number.isRequired
};

const Tables = () => {
    const [isLoading, setLoading] = useState(true);
    const [isOpen, setOpen] = useState(false);
    // const [rows, setRows] = useState([]);

    const [order, setOrder] = React.useState('asc');
    const [orderBy, setOrderBy] = React.useState('calories');
    const [selected, setSelected] = React.useState([]);
    const [page, setPage] = React.useState(0);
    const [dense] = React.useState(false);
    const [rowsPerPage, setRowsPerPage] = React.useState(10);
    const [selectedValue, setSelectedValue] = React.useState([]);

    const [project, setProject] = React.useState('');

    const { insertProductDetails, fetchProductDetails, dataProductDetails, isLoadingProduct } = useAuth();

    // console.log(dataProductDetails?.["data"]?.length)
    let rows = [];
    if (dataProductDetails?.['data']?.length > 0) {
        dataProductDetails?.['data']?.map((data_, index_) => {
            // console.log(data_[Object.keys(data_)[0]])
            rows.push(
                createData(
                    data_[Object.keys(data_)[0]]['main_category'],
                    data_[Object.keys(data_)[0]]['middle_category'],
                    data_[Object.keys(data_)[0]]['subcategory'],
                    data_[Object.keys(data_)[0]]['Name'],
                    data_[Object.keys(data_)[0]]['Price'],
                    data_[Object.keys(data_)[0]]['Source'],
                    data_[Object.keys(data_)[0]]['Url'],
                    data_[Object.keys(data_)[0]]['Info'],
                    data_[Object.keys(data_)[0]]['DataInfo']
                )
            );
        });
        // console.log(dataProductDetails?.["data"][0][Object.keys(dataProductDetails?.["data"][0])])
    }

    // const rows = dataProductDetails?.["data"].map((data_, index_) => {
    //     console.log(data_)
    // })

    useEffect(() => {
        // setLoading(false);
        fetchProductDetails();
    }, []);

    // const rows =  [
    //     createData('Cupcake', 305, 3.7, 67, 4.3),
    //     createData('Donut', 452, 25.0, 51, 4.9),
    //     createData('Eclair', 262, 16.0, 24, 6.0),
    //     createData('Frozen yoghurt', 159, 6.0, 24, 4.0),
    //     createData('Gingerbread', 356, 16.0, 49, 3.9),
    //     createData('Honeycomb', 408, 3.2, 87, 6.5),
    //     createData('Ice cream sandwich', 237, 9.0, 37, 4.3),
    //     createData('Jelly Bean', 375, 0.0, 94, 0.0),
    //     createData('KitKat', 518, 26.0, 65, 7.0),
    //     createData('Lollipop', 392, 0.2, 98, 0.0),
    //     createData('Marshmallow', 318, 0, 81, 2.0),
    //     createData('Nougat', 360, 19.0, 9, 37.0),
    //     createData('Oreo', 437, 18.0, 63, 4.0)
    // ];

    // const newRow = [];
    // rows.forEach((element) => {
    //     newRow.push({
    //         ...element,
    //         history: null
    //     });
    // });

    const fields = [
        {
            // Visible in table header and when matching columns.
            label: 'Main Category',
            // This is the key used for this field when we call onSubmit.
            key: 'main_category',
            // Allows for better automatic column matching. Optional.
            //   alternateMatches: ["first name", "first"],
            // Used when editing and validating information.
            fieldType: {
                // There are 3 types - "input" / "checkbox" / "select".
                type: 'input'
            },
            // Used in the first step to provide an example of what data is expected in this field. Optional.
            example: '의장부 (Ship rig / outfit)',
            // Can have multiple validations that are visible in Validation Step table.
            validations: [
                {
                    // Can be "required" / "unique" / "regex"
                    rule: 'required',
                    errorMessage: 'Main Category is required',
                    // There can be "info" / "warning" / "error" levels. Optional. Default "error".
                    level: 'error'
                }
            ]
        },
        {
            // Visible in table header and when matching columns.
            label: 'Middle Category',
            // This is the key used for this field when we call onSubmit.
            key: 'middle_category',
            // Allows for better automatic column matching. Optional.
            // alternateMatches: ["first name", "first"],
            // Used when editing and validating information.
            fieldType: {
                // There are 3 types - "input" / "checkbox" / "select".
                type: 'input'
            },
            // Used in the first step to provide an example of what data is expected in this field. Optional.
            example: 'Unloading gear',
            // Can have multiple validations that are visible in Validation Step table.
            validations: [
                {
                    // Can be "required" / "unique" / "regex"
                    rule: 'required',
                    errorMessage: 'Middle Category is required',
                    // There can be "info" / "warning" / "error" levels. Optional. Default "error".
                    level: 'error'
                }
            ]
        },
        {
            // Visible in table header and when matching columns.
            label: 'Subcategory',
            // This is the key used for this field when we call onSubmit.
            key: 'subcategory',
            // Allows for better automatic column matching. Optional.
            // alternateMatches: ["first name", "first"],
            // Used when editing and validating information.
            fieldType: {
                // There are 3 types - "input" / "checkbox" / "select".
                type: 'input'
            },
            // Used in the first step to provide an example of what data is expected in this field. Optional.
            example: 'Crane Winch',
            // Can have multiple validations that are visible in Validation Step table.
            validations: [
                {
                    // Can be "required" / "unique" / "regex"
                    rule: 'required',
                    errorMessage: 'Subcategory is required',
                    // There can be "info" / "warning" / "error" levels. Optional. Default "error".
                    level: 'error'
                }
            ]
        },
        {
            // Visible in table header and when matching columns.
            label: 'Name',
            // This is the key used for this field when we call onSubmit.
            key: 'Name',
            // Allows for better automatic column matching. Optional.
            // alternateMatches: ["first name", "first"],
            // Used when editing and validating information.
            fieldType: {
                // There are 3 types - "input" / "checkbox" / "select".
                type: 'input'
            },
            // Used in the first step to provide an example of what data is expected in this field. Optional.
            example: 'Relay Box for Windlasses',
            // Can have multiple validations that are visible in Validation Step table.
            validations: [
                {
                    // Can be "required" / "unique" / "regex"
                    rule: 'required',
                    errorMessage: 'Name is required',
                    // There can be "info" / "warning" / "error" levels. Optional. Default "error".
                    level: 'error'
                }
            ]
        },
        {
            // Visible in table header and when matching columns.
            label: 'Price',
            // This is the key used for this field when we call onSubmit.
            key: 'Price',
            // Allows for better automatic column matching. Optional.
            // alternateMatches: ["first name", "first"],
            // Used when editing and validating information.
            fieldType: {
                // There are 3 types - "input" / "checkbox" / "select".
                type: 'input'
            },
            // Used in the first step to provide an example of what data is expected in this field. Optional.
            example: '€109.20',
            // Can have multiple validations that are visible in Validation Step table.
            validations: [
                {
                    // Can be "required" / "unique" / "regex"
                    rule: 'required',
                    errorMessage: 'Price is required',
                    // There can be "info" / "warning" / "error" levels. Optional. Default "error".
                    level: 'error'
                }
            ]
        },
        {
            label: 'Url',
            key: 'Url',
            fieldType: {
                type: 'input'
            },
            example: '/en/bavaria-relay-box-for-windlasses.html',
            validations: [
                {
                    rule: 'required',
                    errorMessage: 'Url is required',
                    level: 'error'
                }
            ]
        },
        {
            label: 'Source',
            key: 'Source',
            fieldType: {
                type: 'input'
            },
            example: 'svb24',
            validations: [
                {
                    rule: 'required',
                    errorMessage: 'Source is required',
                    level: 'error'
                }
            ]
        },
        {
            label: 'DataInfo',
            key: 'DataInfo',
            fieldType: {
                type: 'input'
            },
            example: '2022-12-29 00:20:00',
            validations: [
                {
                    rule: 'required',
                    errorMessage: 'DataInfo is required',
                    level: 'error'
                }
            ]
        },
        {
            label: 'Info',
            key: 'Info',
            fieldType: {
                type: 'input'
            },
            example:
                'The double-acting high-current relay is ideal for controlling windlasses and other large consumers such as gangways, cranes, electric winches, etc. The relay boxes are compact and simple. Thanks to their external connections, they are very easy to install. Available for 2 and 4 port engines. Dimensions: (WxHxD) 130 x 100 x 70 mm. Protected to IP66.'
        }
    ];

    const onSubmit = (data, file) => {
        insertProductDetails(data);
    };

    const handleRequestSort = (event, property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const handleSelectAllClick = (event) => {
        if (event.target.checked) {
            if (selected.length > 0) {
                setSelected([]);
            } else {
                const newSelectedId = rows.map((n) => n.name);
                setSelected(newSelectedId);
            }
            return;
        }
        setSelected([]);
    };

    const handleClick = (event, name) => {
        const selectedIndex = selected.indexOf(name);
        let newSelected = [];

        if (selectedIndex === -1) {
            newSelected = newSelected.concat(selected, name);
        } else if (selectedIndex === 0) {
            newSelected = newSelected.concat(selected.slice(1));
        } else if (selectedIndex === selected.length - 1) {
            newSelected = newSelected.concat(selected.slice(0, -1));
        } else if (selectedIndex > 0) {
            newSelected = newSelected.concat(selected.slice(0, selectedIndex), selected.slice(selectedIndex + 1));
        }
        const selectedRowData = rows.filter((row) => newSelected.includes(row.Name.toString()));
        setSelectedValue(selectedRowData);
        setSelected(newSelected);
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event?.target.value, 10));
        setPage(0);
    };

    const isSelected = (name) => selected.indexOf(name) !== -1;

    // Avoid a layout jump when reaching the last page with empty rows.
    const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - dataProductDetails?.['data']?.length.length) : 0;

    return (
        <Grid container spacing={gridSpacing}>
            <Grid item xs={12} lg={12} md={6}>
                <MainCard
                    content={false}
                    title="Data Tables"
                    secondary={
                        <Stack direction="row" spacing={2} alignItems="center">
                            <FormControl sx={{ m: 1, minWidth: 150 }}>
                                <InputLabel htmlFor="project-select">Main Category</InputLabel>
                                <Select
                                    id="project"
                                    name="project"
                                    value={project}
                                    onChange={async (e) => {
                                        setProject(e.target.value);
                                    }}
                                    label="Project"
                                >
                                    <MenuItem value="">
                                        <em>None</em>
                                    </MenuItem>
                                    <MenuItem value={'hull'}>Hull</MenuItem>
                                    <MenuItem value={'engine'}>Engine</MenuItem>
                                    <MenuItem value={'electrical-and-electronic'}>Electrical and Electronic</MenuItem>
                                    <MenuItem value={'ship-rig-outfit'}>Ship rig / Outfit</MenuItem>
                                </Select>
                            </FormControl>
                            <Button
                                variant="contained"
                                size="small"
                                onClick={() => {
                                    setOpen(true);
                                }}
                            >
                                Upload
                            </Button>
                            <CSVExport data={selectedValue.length > 0 ? selectedValue : rows} filename="data-tables.csv" header={header} />
                            <SecondaryAction link="https://next.material-ui.com/components/tables/" />
                        </Stack>
                    }
                >
                    {/* table */}
                    <ReactSpreadsheetImport
                        isOpen={isOpen}
                        onClose={() => {
                            setOpen(false);
                        }}
                        onSubmit={onSubmit}
                        fields={fields}
                    />

                    <Paper sx={{ width: '100%', mb: 2 }}>
                        <EnhancedTableToolbar numSelected={selected.length} />

                        {/* table */}
                        <TableContainer>
                            <Table sx={{ minWidth: 750 }} aria-labelledby="tableTitle" size={dense ? 'small' : 'medium'}>
                                <EnhancedTableHead
                                    numSelected={selected.length}
                                    order={order}
                                    orderBy={orderBy}
                                    onSelectAllClick={handleSelectAllClick}
                                    onRequestSort={handleRequestSort}
                                    rowCount={rows.length}
                                />
                                <TableBody>
                                    {stableSort(rows, getComparator(order, orderBy))
                                        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                        .map((row, index) => {
                                            if (typeof row === 'number') return null;
                                            const isItemSelected = isSelected(row.Name);
                                            const labelId = `enhanced-table-checkbox-${index}`;
                                            return (
                                                <TableRow
                                                    hover
                                                    onClick={(event) => handleClick(event, row.Name)}
                                                    role="checkbox"
                                                    aria-checked={isItemSelected}
                                                    tabIndex={-1}
                                                    key={row.Name}
                                                    selected={isItemSelected}
                                                >
                                                    <TableCell padding="checkbox" sx={{ pl: 3 }}>
                                                        <Checkbox
                                                            color="primary"
                                                            checked={isItemSelected}
                                                            inputProps={{
                                                                'aria-labelledby': labelId
                                                            }}
                                                        />
                                                    </TableCell>
                                                    <TableCell component="th" id={labelId} scope="row" padding="normal">
                                                        {row.main_category}
                                                    </TableCell>
                                                    <TableCell component="th" scope="row" padding="normal">
                                                        {row.middle_category}
                                                    </TableCell>
                                                    <TableCell component="th" scope="row" padding="normal">
                                                        {row.subcategory}
                                                    </TableCell>
                                                    <TableCell component="th" scope="row" padding="normal">
                                                        {row.Name}
                                                    </TableCell>
                                                    <TableCell component="th" scope="row" padding="normal">
                                                        {row.Price}
                                                    </TableCell>
                                                    <TableCell component="th" scope="row" padding="normal">
                                                        {row.Url}
                                                    </TableCell>
                                                    <TableCell component="th" scope="row" padding="normal">
                                                        {row.Source}
                                                    </TableCell>
                                                    <TableCell component="th" scope="row" padding="normal">
                                                        {row.DataInfo}
                                                    </TableCell>
                                                    <TableCell component="th" scope="row" padding="normal">
                                                        {row.Info}
                                                    </TableCell>
                                                    {/* <TableCell align="">{row.calories}</TableCell> */}
                                                    {/* <TableCell align="">{row.fat}</TableCell> */}
                                                    {/* <TableCell align="">{row.carbs}</TableCell> */}
                                                    {/* <TableCell sx={{ pr: 3 }} align="">{row.protein}</TableCell> */}
                                                </TableRow>
                                            );
                                        })}
                                    {emptyRows > 0 && (
                                        <TableRow
                                            style={{
                                                height: (dense ? 33 : 53) * emptyRows
                                            }}
                                        >
                                            <TableCell colSpan={6} />
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        {/* table data */}
                        <TablePagination
                            rowsPerPageOptions={[10, 30, 50, 100]}
                            component="div"
                            count={dataProductDetails?.['data']?.length}
                            rowsPerPage={rowsPerPage}
                            page={page}
                            onPageChange={handleChangePage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                        />
                    </Paper>
                </MainCard>
            </Grid>
            <Grid item xs={12}></Grid>
        </Grid>
    );
};

export default Tables;
